import React, { useState, useEffect } from 'react';
import {
  X,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Music,
  Loader,
  ListMusic,
  Star,
  PauseCircle
} from 'lucide-react';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../context/ToastContext';
import './FullScreenPlayer.css';

interface FullScreenPlayerProps {
  isOpen: boolean;
  onClose: () => void;
}

const FullScreenPlayer: React.FC<FullScreenPlayerProps> = ({ isOpen, onClose }) => {
  const {
    state,
    togglePlay,
    nextTrack,
    previousTrack,
    setCurrentTime,
    setVolume,
    playAudio,
    removeFromQueue
  } = useAudioPlayer();

  const { favorites, isFavorite, toggleFavorite } = useFavorites();
  const { showToast } = useToast();
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);
  const [isDraggingVolume, setIsDraggingVolume] = useState(false);
  const [activeTab, setActiveTab] = useState<'queue' | 'favorites'>(
    state.playlistSource === 'favorites' ? 'favorites' : 'queue'
  );

  // Update active tab when playlist source changes or player opens
  useEffect(() => {
    if (isOpen) {
      // When player opens, set tab based on current playlist source
      setActiveTab(state.playlistSource === 'favorites' ? 'favorites' : 'queue');
    }
  }, [isOpen, state.playlistSource]);

  // Lock body scroll when fullscreen player is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;

      // Apply styles to lock scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore scroll when closing
        const savedScrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';

        // Restore scroll position
        if (savedScrollY) {
          window.scrollTo(0, parseInt(savedScrollY.replace('-', '').replace('px', ''), 10));
        }
      };
    }
  }, [isOpen]);

  const handleToggleFavorite = (audio: any) => {
    // Find the full audio object if we only have the ID
    const fullAudio = typeof audio === 'number'
      ? state.queue.find(a => a.id === audio) || state.currentAudio || favorites.find(f => f.id === audio)
      : audio;

    if (fullAudio) {
      toggleFavorite(fullAudio);
      const action = isFavorite(fullAudio.id) ? 'Removed from' : 'Added to';
      showToast(`${action} favorites`, 'success');
    }
  };

  // Handle global mouse and touch events for dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isDraggingProgress) {
        const progressBar = document.querySelector('.fs-progress-bar') as HTMLElement;
        if (!progressBar) return;

        const rect = progressBar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const width = rect.width;
        const percentage = Math.max(0, Math.min(1, clickX / width));
        const newTime = percentage * state.duration;
        setCurrentTime(newTime);
      }

      if (isDraggingVolume) {
        const volumeBar = document.querySelector('.fs-volume-bar') as HTMLElement;
        if (!volumeBar) return;

        const rect = volumeBar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const width = rect.width;
        const newVolume = Math.max(0, Math.min(1, clickX / width));
        setVolume(newVolume);
      }
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (isDraggingProgress) {
        const progressBar = document.querySelector('.fs-progress-bar') as HTMLElement;
        if (!progressBar) return;

        const touch = e.touches[0];
        const rect = progressBar.getBoundingClientRect();
        const clickX = touch.clientX - rect.left;
        const width = rect.width;
        const percentage = Math.max(0, Math.min(1, clickX / width));
        const newTime = percentage * state.duration;
        setCurrentTime(newTime);
      }

      if (isDraggingVolume) {
        const volumeBar = document.querySelector('.fs-volume-bar') as HTMLElement;
        if (!volumeBar) return;

        const touch = e.touches[0];
        const rect = volumeBar.getBoundingClientRect();
        const clickX = touch.clientX - rect.left;
        const width = rect.width;
        const newVolume = Math.max(0, Math.min(1, clickX / width));
        setVolume(newVolume);
      }
    };

    const handleGlobalMouseUp = () => {
      setIsDraggingProgress(false);
      setIsDraggingVolume(false);
    };

    const handleGlobalTouchEnd = () => {
      setIsDraggingProgress(false);
      setIsDraggingVolume(false);
    };

    if (isDraggingProgress || isDraggingVolume) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('touchend', handleGlobalTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [isDraggingProgress, isDraggingVolume, state.duration, setCurrentTime, setVolume]);

  if (!isOpen) return null;

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0]?.clientX || e.changedTouches[0]?.clientX : e.clientX;
    const clickX = clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(1, clickX / width));
    const newTime = percentage * state.duration;
    setCurrentTime(newTime);
  };

  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDraggingProgress(true);
    handleProgressClick(e);
  };

  const handleProgressTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDraggingProgress(true);
    handleProgressClick(e);
  };

  const handleProgressTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDraggingProgress) return;
    e.preventDefault(); // Prevent scrolling while dragging
    handleProgressClick(e);
  };

  const handleProgressTouchEnd = () => {
    setIsDraggingProgress(false);
  };

  const handleVolumeClick = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0]?.clientX || e.changedTouches[0]?.clientX : e.clientX;
    const clickX = clientX - rect.left;
    const width = rect.width;
    const newVolume = Math.max(0, Math.min(1, clickX / width));
    setVolume(newVolume);
  };

  const handleVolumeMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDraggingVolume(true);
    handleVolumeClick(e);
  };

  const handleVolumeTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDraggingVolume(true);
    handleVolumeClick(e);
  };

  const handleVolumeTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDraggingVolume) return;
    e.preventDefault(); // Prevent scrolling while dragging
    handleVolumeClick(e);
  };

  const handleVolumeTouchEnd = () => {
    setIsDraggingVolume(false);
  };

  const progressPercentage = state.duration ? (state.currentTime / state.duration) * 100 : 0;
  const volumePercentage = state.volume * 100;

  const handlePlayFromQueue = (audio: typeof state.queue[0], index: number) => {
    // If clicking on currently playing track, just toggle play/pause
    if (state.currentAudio?.id === audio.id && currentPlayingIndex === index) {
      togglePlay();
    } else {
      playAudio(audio, state.queue);
    }
  };

  const currentPlayingIndex = state.currentAudio ? state.queue.findIndex(item => item.id === state.currentAudio?.id) : -1;

  return (
    <div className="fullscreen-player">
      <div className="fs-background">
        <div className="fs-gradient-overlay"></div>
        {state.isPlaying && (
          <div className="fs-audio-visualizer">
            <div className="wave-container">
              {Array.from({ length: 50 }).map((_, i) => (
                <div
                  key={i}
                  className="wave-bar"
                  style={{
                    animationDelay: `${i * 0.05}s`,
                    height: `${Math.random() * 60 + 20}%`
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="fs-header">
        <button className="fs-close-btn" onClick={onClose}>
          <X size={24} />
        </button>
      </div>

      <div className="fs-content">
        <div className="fs-main-section">
          {state.currentAudio ? (
            <>
              <div className="fs-album-art">
                {state.currentAudio.naatKhawan?.image ? (
                  <img
                    src={state.currentAudio.naatKhawan.image}
                    alt={state.currentAudio.naatKhawan.name}
                    className={state.isPlaying ? 'playing' : ''}
                  />
                ) : (
                  <div className={`fs-album-placeholder ${state.isPlaying ? 'playing' : ''}`}>
                    <Music size={120} />
                  </div>
                )}
              </div>

              <div className="fs-track-info">
                <h1 className="fs-track-title">{state.currentAudio.title}</h1>
                {state.currentAudio.urduTitle && (
                  <h2 className="fs-track-urdu" dir="rtl">{state.currentAudio.urduTitle}</h2>
                )}
                <p className="fs-track-artist">{state.currentAudio.naatKhawan?.name}</p>
              </div>

              <div className="fs-controls-section">
                <div className="fs-progress-section">
                  <span className="fs-time">{formatTime(state.currentTime)}</span>
                  <div
                    className={`fs-progress-bar ${isDraggingProgress ? 'dragging' : ''}`}
                    onClick={handleProgressClick}
                    onMouseDown={handleProgressMouseDown}
                    onTouchStart={handleProgressTouchStart}
                    onTouchMove={handleProgressTouchMove}
                    onTouchEnd={handleProgressTouchEnd}
                  >
                    <div className="fs-progress-track">
                      <div
                        className="fs-progress-fill"
                        style={{ width: `${progressPercentage}%` }}
                      />
                      <div
                        className="fs-progress-handle"
                        style={{ left: `${progressPercentage}%` }}
                      />
                    </div>
                  </div>
                  <span className="fs-time">{formatTime(state.duration)}</span>
                </div>

                <div className="fs-control-buttons">
                  <button
                    className="fs-control-btn"
                    onClick={previousTrack}
                    disabled={state.currentIndex <= 0}
                  >
                    <SkipBack size={32} />
                  </button>

                  <button
                    className="fs-play-pause-btn"
                    onClick={togglePlay}
                    disabled={state.isLoading}
                  >
                    {state.isLoading ? (
                      <Loader size={40} className="spinning" />
                    ) : state.isPlaying ? (
                      <Pause size={40} />
                    ) : (
                      <Play size={40} />
                    )}
                  </button>

                  <button
                    className="fs-control-btn"
                    onClick={nextTrack}
                    disabled={state.currentIndex >= state.playlist.length - 1}
                  >
                    <SkipForward size={32} />
                  </button>
                </div>

                <div className="fs-bottom-controls">
                  <button
                    className={`fs-favorite-btn ${isFavorite(state.currentAudio.id) ? 'active' : ''}`}
                    onClick={() => handleToggleFavorite(state.currentAudio!)}
                  >
                    <Star size={24} fill={isFavorite(state.currentAudio.id) ? 'currentColor' : 'none'} />
                  </button>

                  <div className="fs-volume-control">
                    <button
                      className="fs-volume-btn"
                      onClick={() => setVolume(state.volume === 0 ? 1 : 0)}
                    >
                      {state.volume === 0 ? <VolumeX size={24} /> : <Volume2 size={24} />}
                    </button>
                    <div
                      className={`fs-volume-bar ${isDraggingVolume ? 'dragging' : ''}`}
                      onClick={handleVolumeClick}
                      onMouseDown={handleVolumeMouseDown}
                      onTouchStart={handleVolumeTouchStart}
                      onTouchMove={handleVolumeTouchMove}
                      onTouchEnd={handleVolumeTouchEnd}
                    >
                      <div className="fs-volume-track">
                        <div
                          className="fs-volume-fill"
                          style={{ width: `${volumePercentage}%` }}
                        />
                        <div
                          className="fs-volume-handle"
                          style={{ left: `${volumePercentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="fs-no-track">
              <Music size={80} />
              <p>No track playing</p>
            </div>
          )}
        </div>

        <div className="fs-queue-section">
          <div className="fs-tabs">
            <button
              className={`fs-tab ${activeTab === 'queue' ? 'active' : ''}`}
              onClick={() => setActiveTab('queue')}
            >
              <ListMusic size={18} />
              Queue ({state.queue.length})
            </button>
            <button
              className={`fs-tab ${activeTab === 'favorites' ? 'active' : ''}`}
              onClick={() => setActiveTab('favorites')}
            >
              <Star size={18} />
              Favorites ({favorites.length})
            </button>
          </div>
          <div className="fs-queue-list">
            {activeTab === 'queue' ? (
              state.queue.length === 0 ? (
                <div className="fs-empty-queue">
                  <Music size={48} />
                  <p>Queue is empty</p>
                </div>
              ) : (
                state.queue.map((audio, index) => {
                  const isCurrentlyPlaying = currentPlayingIndex === index;
                  return (
                    <div
                      key={`${audio.id}-${index}`}
                      className={`fs-queue-item ${isCurrentlyPlaying ? 'playing' : ''}`}
                      onClick={() => handlePlayFromQueue(audio, index)}
                    >
                      <div className="fs-queue-item-number">
                    {currentPlayingIndex === index ? (
                      state.isPlaying ? (
                        <div className="fs-playing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      ) : (
                        <PauseCircle size={20} className="fs-pause-indicator" />
                      )
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="fs-queue-item-info">
                    <span className="fs-queue-item-title">{audio.title}</span>
                    {audio.urduTitle && (
                      <span className="fs-queue-item-urdu" dir="rtl">{audio.urduTitle}</span>
                    )}
                    <span className="fs-queue-item-artist">{audio.naatKhawan?.name}</span>
                  </div>
                  <button
                    className={`fs-queue-favorite-btn ${isFavorite(audio.id) ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFavorite(audio);
                    }}
                  >
                    <Star size={18} fill={isFavorite(audio.id) ? 'currentColor' : 'none'} />
                  </button>
                  <button
                    className="fs-queue-remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromQueue(index);
                    }}
                  >
                    <X size={18} />
                  </button>
                    </div>
                  );
                })
              )
            ) : (
              // Favorites Tab
              favorites.length === 0 ? (
                <div className="fs-empty-queue">
                  <Star size={48} />
                  <p>No favorites yet</p>
                  <span style={{ fontSize: '0.9rem', opacity: 0.7 }}>Add naats to favorites to see them here</span>
                </div>
              ) : (
                favorites.map((audio, index) => {
                  const isPlaying = state.currentAudio?.id === audio.id;
                  return (
                    <div
                      key={`fav-${audio.id}-${index}`}
                      className={`fs-queue-item ${isPlaying ? 'playing' : ''}`}
                      onClick={() => {
                        // If clicking on currently playing track, just toggle play/pause
                        if (isPlaying && state.playlistSource === 'favorites') {
                          togglePlay();
                        } else {
                          playAudio(audio, favorites, 'favorites');
                        }
                      }}
                    >
                      <div className="fs-queue-item-number">
                        {isPlaying ? (
                          state.isPlaying ? (
                            <div className="fs-playing-indicator">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          ) : (
                            <PauseCircle size={20} className="fs-pause-indicator" />
                          )
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </div>
                      <div className="fs-queue-item-info">
                        <span className="fs-queue-item-title">{audio.title}</span>
                        {audio.urduTitle && (
                          <span className="fs-queue-item-urdu" dir="rtl">{audio.urduTitle}</span>
                        )}
                        <span className="fs-queue-item-artist">{audio.naatKhawan?.name}</span>
                      </div>
                      <button
                        className={`fs-queue-favorite-btn active`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(audio);
                        }}
                      >
                        <Star size={18} fill="currentColor" />
                      </button>
                    </div>
                  );
                })
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullScreenPlayer;